import { createGlobalState } from 'react-hooks-global-state';
// Safe as a static import: common/connection depends only on the analytics and
// activity-log modules, neither of which imports this one, so there is no cycle.
import { getConnectionState } from 'src/common/connection';
import { logError } from 'src/common/frontendLog';

/**
 * Severity bands of the Claims review panel, percent scale, edges shared
 * between adjacent bands. Used whenever the API's `severity_array` is missing
 * or empty so the finding card always resolves a level.
 */
export const DEFAULT_SEVERITY_ARRAY = [
  { label: 'Critical', min: 0, max: 50 },
  { label: 'High', min: 50, max: 70 },
  { label: 'Medium', min: 70, max: 80 },
  { label: 'Low', min: 80, max: 100 }
];

const {
  setGlobalState: setGlobalStateRaw,
  useGlobalState,
  getGlobalState
} = createGlobalState({
  reload: true,
  modaltype: null,
  modalmessage: null,
  sessionerror: null,
  loggedin: false,
  charts1: true,
  charts2: true,
  extractdoc: null,
  exportdoc: true,
  caseName: '',
  resultcase: null,
  resultdoc: null,
  resultfile: null,
  resultfilename: null,
  resultshow: false,
  resultreload: true,
  PIIKeyword: null,
  PIIKeywordstatus: false,
  esearch: null,
  PIICase: null,
  FileExplorer: false,
  ReviewTools: false,
  PIItag: null,
  PIIstartoffsets: null,
  PIIendoffsets: null,
  PIItagcolor: null,
  PIIcontent: null,
  PIIOldTag: null,
  SavePIIs: false,
  ResetPIIs: false,
  DeletePIIs: false,
  AllPIITags: null,
  ReviewEmbedType: null,
  ReviewFolderID: null,
  SearchParams: null,
  SearchReload: false,
  SearchType: 1,
  SearchBarWidth: '60px',
  AdvanceQuery: {},
  ShowCoding: true,
  UpdatePIIS: false,
  CloseModal: false,
  relationTag: null,
  relationStart: null,
  relationEnd: null,
  relationRow: null,
  tagRow: null,
  relationtext: null,
  relationselected: false,
  relationLabel: null,
  RelationPIIs: null,
  RelationPIIsDefault: null,
  PIICounter: 0,
  RemoveRelation: false,
  OriginalRelation: null,
  OriginalText: null,
  UploadedPIIids: '',
  UploadedPIICount: 0,
  DownloadPIIs: false,
  ExportPIIs: false,
  PIIReviewer: false,
  modelType: 1,
  AdditionalPIIRow: null,
  AdditionalPIITag: null,
  AdditionalPIIText: null,
  AdditionalPIIstart: null,
  AdditionalPIIend: null,
  removeStart: null,
  removeEnd: null,
  removeRow: null,
  removeText: null,
  removeTag: null,
  removeCount: null,
  TagginArray: null,
  PIIColors: null,
  rerelation: null,
  DeleteCase: true,
  Config: null,
  BucketType: null,
  BucketPath: null,
  accessKeyId: null,
  secretAccessKey: null,
  BucketRegion: null,
  ErrorFoundGlobal: true,
  improvementFounds: 0,
  issueLine: 1,
  issueSelect: false,
  BrandsFounds: 0,
  BrandsLine: 1,
  BrandsSelect: false,
  AllLine: 1,
  AllSelect: false,
  languageType: 'EnglishCommercial',
  languageArray: [],
  isiEditorClick: null,
  ThumbArray: [],
  openTagModal: 0,
  processServicesStatus: false,
  // Bumped (to Date.now()) to force DefaultHeader to re-check MLRC service
  // status — e.g. on file upload / tactic-details change.
  serviceStatusRecheck: 0,
  DownloadAllCSV: false,
  DownloadAllStatus: false,
  updateAllTab: false,
  updateClaimsTab: false,
  updateBrandsTab: false,
  updateChineseTab: false,
  ExportRulesStatus: false,
  ExportActivityLogs: false,
  smartSerachAccessEmails: [],
  refreshQuestionsSidebar: 0,
  openTagModalTactics: 0,
  openConfirmTactic: 0,
  // Set by the upload panel when a batch lands, so the document grid can open
  // Tactic Details on the document that was ACTUALLY just created rather than
  // on whatever happens to be the top row. The grid resolves it to a real docId
  // (via openTagModalTactics) once the new row arrives and then clears this.
  // See AllFiles/index.tsx `applyDeltaUpdate`.
  openTacticsForNewUpload: false,
  AllSelectISI: false,
  isiLine: 1,
  refLine: 1,
  AllSelectRef: false,
  LIBRARY_FILE_FILTER_RULES: [],
  UploadedFiles: [],
  ProcessFile: false,
  RemovecId: 0,
  RemoveHighlight: false,
  processFilesIds: '',
  processFiles: false,
  createServiceFirst: false,
  headerMenu: 0,
  collapse: true,
  expandRows: false,
  rowNumber: 10000000000000000000000000000,
  refreshRowClick: 0,
  HighlightFilenames: '',
  UserHighlight: null,
  UserHighlightUpdate: false,
  NonclickableID: 0,
  ReloadClaims: false,
  DeleteId: 0,
  DeleteHighlight: false,
  // User-highlight delete: triggered from the User Highlight hover card's
  // cross button (after the confirm modal). DeleteUserHighlightData carries
  // the target { userHLId, parentId } so annotate.tsx removes only that
  // user highlight and calls the delete API.
  DeleteUserHighlight: false,
  DeleteUserHighlightData: null,
  // Signal fired after a user highlight is successfully deleted, so index.tsx
  // can drop it from userHighlightArray (updates sidebar + PDF) and decrement
  // the counts. Carries { parentId, cId, ts }.
  userHighlightDeleted: null,
  rulesArray: [],
  PlusClaim: false,
  DeleteClaim: false,
  DeleteCount: 0,
  Updatesidebar: false,
  UpdatesidebarArray: [],
  Reducesidebar: false,
  ReduceParentId: 0,
  deletedThumbsArray: [],
  stopFile: true,
  getImageId: 0,
  selectedproductType: '',
  claimOptions: [],
  userRoles: [],
  // Demo-role feature limits fetched from API.roleLimits (null until loaded /
  // not a demo user). Shape: { role, roleId, features: [{ key, limits }] }.
  roleLimits: null,
  productListingArray: [],
  cloudStorageVisibility: false,
  // AWS creds/region (decrypted from API.S3UploadPath) — used for client-side
  // Textract OCR of drawn image regions.
  awsCreds: null,
  // In-progress (drawn-but-not-committed) supporting-sentence boxes on the claim
  // review PDF, preserved so switching tabs (Claims ↔ ISI/References) — which
  // unmounts and remounts the claim highlighter — doesn't discard them. Shape:
  // { filename, boxes } so a different document never inherits stale boxes.
  claimPendingBoxes: null,
  // Problematic-score window used to gate whether Claim/reason/expected/primary
  // fields render in the review sidebars. The backend now supplies this range
  // per document (min_problematic_to_show / max_problematic_to_show on
  // viewerSQL for Views, docsRealTimeSQL for copyPaste). Defaults preserve the
  // 0.55–0.8 window when the API omits the keys.
  minProblematicToShow: 0.55,
  maxProblematicToShow: 0.8,
  // Severity bands of the Claims review panel, supplied per document as
  // `severity_array` on viewerSQL. Percent scale, edges shared between bands.
  // Falls back to DEFAULT_SEVERITY_ARRAY while the API has not answered or
  // omits the key, so the finding card always has a level to show.
  severityArray: DEFAULT_SEVERITY_ARRAY
});

/**
 * `ErrorFoundGlobal` is a confusingly-named *health* flag: `true` means "render
 * the normal page", `false` swaps in the full-screen error page. Around thirty
 * call sites across the dashboard flip it to `false` from an API catch block.
 *
 * That is right for a real fault and wrong for an outage. When the review services
 * are 5xx-ing while they power on, the connectivity bar already says so, offers
 * "Try now", and disappears by itself once the services answer — whereas the error
 * page unmounts the whole app (bar included) and leaves the user to guess. So the
 * flip is suppressed for exactly as long as the bar is up.
 *
 * Enforced here rather than at each call site deliberately: this is an invariant
 * of the flag, not a rule each caller should have to remember, and thirty
 * independently-maintained copies of it would not survive.
 *
 * Nothing else is intercepted, and a genuine failure while the connection is fine
 * still shows the error page exactly as before.
 */
const setGlobalState: typeof setGlobalStateRaw = (key, value) => {
  if (key === 'ErrorFoundGlobal' && value === false) {
    const state = getConnectionState();
    if (state === 'offline' || state === 'lost' || state === 'unavailable') return;
    // The user is about to be shown the error screen. This is the OTHER way that
    // UI appears — a render crash goes through GlobalErrorBoundary and is logged
    // as `react_crash`, but this softer path (an API catch flipping the flag) used
    // to swap the section out with nothing recorded anywhere, so a support report
    // of "it just showed an error" had no matching entry.
    //
    // Only on the true -> false transition: several of the ~30 call sites sit in
    // effects that can fire repeatedly, and logging each one would flood the
    // buffer with duplicates of a single visible event.
    if (getGlobalState('ErrorFoundGlobal') !== false) {
      logError('error_screen_shown', {
        msg: 'Showed the error screen — a request failed while the connection was fine',
        reason: 'error_flag_cleared',
        extra: {
          connection: state,
          href: typeof window !== 'undefined' ? window.location.href : undefined
        }
      });
    }
  }
  return setGlobalStateRaw(key, value);
};

export { useGlobalState, setGlobalState, getGlobalState };
